import { FC, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'

import * as ROUTES from 'src/constants/routes'

import { Header } from './header'
import { Sider } from './sider'
import { StyledContent, StyledMain, StyledMobileContent } from './styled'
import { StyledLayout } from '../styled'
import { HeaderProvider, useHeader } from './context/HeaderContext'
import { BottomTabBar } from './bottomTabBar'
import { usePendingRequests } from 'src/hooks/usePendingRequests'
import { RouteTracker } from 'src/components/analytics/RouteTracker'

interface Props {
  children: React.ReactNode
}

const PATHS_WITH_HEADER = [ROUTES.PRIVATE_MAP, ROUTES.PRIVATE_CALENDAR, ROUTES.PRIVATE_CHAT]

const LayoutContent: FC<Props> = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true)
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { title } = useHeader()
  const showHeader = PATHS_WITH_HEADER.some((path) => location.pathname.startsWith(path))
  const isInChatDetail =
    location.pathname.startsWith(ROUTES.PRIVATE_CHAT) && !!searchParams.get('conversationId')

  // Enable global polling for pending requests
  // This will automatically update Redux badge counts every 30 seconds
  // and on window focus changes
  usePendingRequests({ enablePolling: true, pollingInterval: 30000 })

  const handleSidebarCollapse = (collapsed: boolean) => {
    setSidebarCollapsed(collapsed)
  }

  return (
    <StyledLayout>
      <RouteTracker />
      {showHeader && <Header title={title} />}
      <Sider collapsed={sidebarCollapsed} onCollapse={handleSidebarCollapse} />
      <StyledMain>
        <StyledContent>
          <StyledMobileContent $withHeader={showHeader}>{children}</StyledMobileContent>
        </StyledContent>
        {!isInChatDetail && <BottomTabBar />}
      </StyledMain>
    </StyledLayout>
  )
}

export const Layout: FC<Props> = ({ children }) => {
  return (
    <HeaderProvider>
      <LayoutContent>{children}</LayoutContent>
    </HeaderProvider>
  )
}

// Export the hook for easy access
export { usePageTitle, useAutoPageTitle } from './hooks/usePageTitle'
